import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { get } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import contentService from "../../services/content";
import queryDefault from "../../utils/queryDefault";

export default function ContentCreate() {
  const { id } = useParams();
  const notification = useNotification();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);
  const schema = yup.object().shape({
    type: yup.string().required("type is a required field"),
  });
  const form = useForm({
    defaultValues: {
      type: "",
      content: "",
      align: "",
      order: 0,
      username: user.username,
      img_path: null,
    },
    resolver: yupResolver(schema),
  });

  const type = form.watch("type");

  const typeList = useQuery("content-type-list", () =>
    contentService.getContentType()
  );

  const createContent = useMutation("create_content", contentService.create, {
    onSuccess: () => {
      navigate(`/app/cms/blog/detail/${id}`);

      notification.onOpen({
        message: "Content was successfully created!",
        type: "success",
        position: "top",
      });
    },
    onError: (error: any) => {
      console.log(error);
      notification.onOpen({
        message: error?.response?.data?.message || "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: any) => {
    const formData = new FormData();
    formData.append("blog_id", id || "");
    formData.append("type", values.type);
    formData.append("content", values.content);
    formData.append("align", values.align);
    formData.append("order", values.order);
    formData.append("username", user.username);
    if (values.img_path && values.img_path[0]) {
      formData.append("file", values.img_path[0]); // Append file to FormData
    }
    createContent.mutate(formData);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Create Content"
        breadcrumbs={[
          { label: "Blog", href: "/app/cms/blog" },
          { label: "Blog Detail", href: `/app/cms/blog/detail/${id}` },
          {
            label: "Create Content",
            href: `/app/cms/blog/${id}/content/create`,
          },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="blog"
        requiredPermissions={{ is_create: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Type</FormLabel>
                <Controller
                  name="type"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        {get(typeList, ["data", "data"], []).map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.type} key={item?.type}>
                              {item?.desc}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
              {type === "img" ? (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel>Image</FormLabel>
                  <Controller
                    name="img_path"
                    control={form.control}
                    render={({ field: { ref, ...field } }) => (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => field.onChange(e.target.files)}
                        ref={ref}
                        style={{ marginTop: 8 }}
                      />
                    )}
                  />
                  {form.formState.errors.img_path && (
                    <p style={{ color: "red" }}>
                      {form.formState.errors.img_path.message}
                    </p>
                  )}
                </FormControl>
              ) : (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel>Content</FormLabel>
                  <Controller
                    name="content"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        multiline
                        rows={4}
                      />
                    )}
                  />
                </FormControl>
              )}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Align</FormLabel>
                <Controller
                  name="align"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value="left" key="left">
                          Align Left
                        </MenuItem>
                        <MenuItem value="right" key="right">
                          Align Right
                        </MenuItem>
                        <MenuItem value="center" key="center">
                          Align Center
                        </MenuItem>
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Order</FormLabel>
                <Controller
                  name="order"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={createContent.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
